import { useMemo, useState } from 'react'
import { createCatalogItem, updateCatalogItem } from '~/api/catalog'
import type { CatalogItem, CatalogItemInput } from '~/api/types'
import { FieldListManager } from '~/components/catalog/catalog-item-editor/FieldListManager'
import { GuidanceSection } from '~/components/catalog/catalog-item-editor/GuidanceSection'
import { ItemBasicForm } from '~/components/catalog/catalog-item-editor/ItemBasicForm'
import { ScoringPanel } from '~/components/catalog/catalog-item-editor/ScoringPanel'
import {
    EMPTY,
    toInput,
    type FieldKey,
} from '~/components/catalog/catalog-item-editor/constants'
import { ConfirmDialog } from '~/components/ConfirmDialog'
import { Button } from '~/components/ui/button'
import { Label } from '~/components/ui/label'
import { useToast } from '~/components/ui/toast'

// 카탈로그 항목 편집 슬라이드오버(KRDS). 매뉴얼 개정 시 superadmin이 항목을
// 직접 수정/추가한다. maxScore/scoringLogic/isDeterministic은 평가 채점에 직접 반영된다.
// 이 컴포넌트는 상태·로드·저장·검증 오케스트레이션만 담당하고, 입력 영역은
// catalog-item-editor/ 하위 컴포넌트(ItemBasicForm·ScoringPanel·GuidanceSection·
// FieldListManager)로 분리했다.

export function CatalogItemEditor({
    item,
    onClose,
    onSaved,
}: {
    // item === null 이면 새 항목 추가 모드.
    item: CatalogItem | null
    onClose: () => void
    onSaved: (message: string) => void
}) {
    const toast = useToast()
    const isCreate = item === null
    const initial = useMemo(() => (item ? toInput(item) : EMPTY), [item])

    const [form, setForm] = useState<CatalogItemInput>(initial)
    const [confirming, setConfirming] = useState(false)
    const [saving, setSaving] = useState(false)

    const set = <K extends FieldKey>(key: K, value: CatalogItemInput[K]) => {
        setForm((f) => ({ ...f, [key]: value }))
    }
    const isDirty = (key: FieldKey) => form[key] !== initial[key]
    const anyDirty = (Object.keys(form) as FieldKey[]).some(isDirty)

    // 필수값 검증: part/classification/scale/title/maxScore.
    const errors: Partial<Record<FieldKey, string>> = {}
    if (!form.part.trim()) errors.part = '파트는 필수예요.'
    if (!form.classification.trim()) errors.classification = '분류는 필수예요.'
    if (!form.scale.trim()) errors.scale = '규모는 필수예요.'
    if (!form.title.trim()) errors.title = '제목은 필수예요.'
    if (form.maxScore === null || Number.isNaN(form.maxScore))
        errors.maxScore = '배점은 필수예요.'
    const hasErrors = Object.keys(errors).length > 0

    const revert = () => {
        setForm(initial)
    }

    const save = async () => {
        setConfirming(false)
        setSaving(true)
        try {
            if (isCreate) {
                await createCatalogItem(form)
                onSaved('새 항목을 추가했어요.')
            } else {
                await updateCatalogItem(item.id, form)
                onSaved('항목을 저장했어요.')
            }
        } catch (e) {
            toast.error((e as Error).message)
            setSaving(false)
        }
    }

    return (
        <div className='fixed inset-0 z-50 flex justify-end'>
            <button
                type='button'
                aria-label='닫기'
                className='bg-background/60 absolute inset-0'
                onClick={onClose}
            />
            <div className='border-border bg-card relative flex h-full w-full max-w-3xl flex-col border-l shadow-lg'>
                <div className='border-border flex items-center justify-between border-b p-4'>
                    <h2 className='krds-title text-foreground'>
                        {isCreate ? '새 항목 추가' : '항목 편집'}
                    </h2>
                    <Button variant='ghost' size='sm' onClick={onClose}>
                        닫기
                    </Button>
                </div>

                <div className='flex-1 space-y-6 overflow-y-auto p-5'>
                    {/* 채점 영향 안내 */}
                    <div className='bg-warning-subtle border-warning/30 rounded-[var(--radius)] border p-3'>
                        <p className='text-warning text-sm'>
                            이 항목은 평가 채점에 직접 반영돼요. 특히{' '}
                            <strong>배점·채점 로직·결정형</strong> 변경은 평가
                            점수와 모니터링 판정에 영향을 줍니다.
                        </p>
                    </div>

                    {/* id (수정 모드만, 읽기전용·복사) */}
                    {!isCreate ? (
                        <div>
                            <Label>항목 ID</Label>
                            <div className='mt-1 flex items-center gap-2'>
                                <code className='border-border bg-muted text-foreground flex-1 truncate rounded-[var(--radius)] border px-3 py-2 font-mono text-xs'>
                                    {item.id}
                                </code>
                                <Button
                                    variant='outline'
                                    size='sm'
                                    onClick={() =>
                                        navigator.clipboard?.writeText(item.id)
                                    }>
                                    복사
                                </Button>
                            </div>
                            <p className='text-muted-foreground mt-1 text-xs'>
                                ID는 수정할 수 없어요.
                            </p>
                        </div>
                    ) : null}

                    <ItemBasicForm
                        form={form}
                        set={set}
                        isDirty={isDirty}
                        errors={errors}
                    />

                    <ScoringPanel form={form} set={set} isDirty={isDirty} />

                    <GuidanceSection form={form} set={set} isDirty={isDirty} />

                    <FieldListManager item={item} />
                </div>

                {/* 하단 액션바 */}
                <div className='border-border flex items-center justify-between gap-2 border-t p-4'>
                    <span className='text-muted-foreground text-xs'>
                        {anyDirty ? '변경된 필드가 강조돼요.' : '변경 없음'}
                    </span>
                    <div className='flex gap-2'>
                        <Button
                            variant='outline'
                            size='sm'
                            disabled={!anyDirty || saving}
                            onClick={revert}>
                            원복
                        </Button>
                        <Button
                            size='sm'
                            disabled={hasErrors || saving || !anyDirty}
                            onClick={() => setConfirming(true)}>
                            {saving ? '저장 중…' : isCreate ? '추가' : '저장'}
                        </Button>
                    </div>
                </div>
            </div>

            <ConfirmDialog
                open={confirming}
                title={isCreate ? '새 항목 추가' : '항목 저장'}
                description='이 변경은 평가 채점/모니터링에 영향을 줍니다. 저장할까요?'
                confirmLabel={isCreate ? '추가' : '저장'}
                danger
                onConfirm={save}
                onCancel={() => setConfirming(false)}
            />
        </div>
    )
}
